import {
  ApplicationLicense,
  Machine,
  ScratchpadBucket,
} from '../../../../../api/graphql/generated';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { logger } from '../../../../../util/logger';
import {
  ApplicationDefaultParameterSetData,
  ApplicationDefaultParameterSetTagToEquipmentMapping,
  Flow,
  FlowItem,
  FlowItemParameterRef,
  Tool,
} from '../../../shared/model/flow.types';
import { AbstractFlowService } from '../../../shared/service/abstract-flow.service';
import {
  FlowItemService,
  FlowRuleService,
} from '../../../shared/service/service.types';
import { BeaconCuttingData } from '../model/beacon-cutting-data';

class CuttingFlowServiceImpl extends AbstractFlowService<BeaconCuttingData.FlowConfig> {
  constructor(
    private license: ApplicationLicense,
    flowItemService: FlowItemService<BeaconCuttingData.FlowConfig>,
    flowRuleService: FlowRuleService,
    tools: Tool[],
    protected machine: Machine
  ) {
    super(
      tools,
      flowItemService,
      flowRuleService,
      BeaconCuttingData.FlowValidationRules,
      machine
    );
  }

  public getDefaultFlowConfig(): BeaconCuttingData.FlowConfig {
    const license = this.getLicense();

    const tools = this.getToolsForAssignedEquipment();
    if (tools.length === 0) {
      logger.error('no tools found', { license });
    }

    const defaultTool = tools[0] ?? {};

    const equipmentConfig = this.getFlowConfigForEquipment();

    return {
      home: {
        waypoint: {
          x: 0.174,
          y: -0.692,
          z: 0.253,
          rx: 1.762,
          ry: 1.762,
          rz: 0.725,
        },
        qnear: [1.57093, -1.57093, 1.57093, -1.57093, -1.57093, 1.57093],
      },
      ioMapping: {
        input: {
          capture: 'TOOL_1',
        },
        output: {
          light: 'TOOL_1',
        },
      },
      torch: {
        ...defaultTool.config,
      },
      materialType: BeaconCuttingData.Material.STEEL,
      materialThickness: BeaconCuttingData.MaterialThickness['1/2'],
      betweenSpeed: 0.25,
      cutting: {
        model: license.cutter,
        ioMap: {
          mode: 'DO5',
          trigger: 'DO4',
        },
      },
      pierceHeightHoldTime: 0,
      gas: BeaconCuttingData.Gas.AIR,
      current: BeaconCuttingData.Current.A_85,
      ...equipmentConfig,
    };
  }

  public async isFlowValidForTeaching(flow: Flow): Promise<boolean> {
    const configValid = flow.config !== undefined;
    const applicationDefaultParameterSet =
      flow.defaultParameterSet !== undefined;

    return configValid && applicationDefaultParameterSet;
  }

  public getAvailableFlowItemParameterRefs(): FlowItemParameterRef[] {
    return Object.values(BeaconCuttingData.FlowItemParameterRef);
  }

  public getApplicationDefaultParameterSetTagToConfig(): Record<
    string,
    string
  > {
    return BeaconCuttingData.ApplicationDefaultParameterSetTagToConfig;
  }

  public getApplicationDefaultParameterSetTagToEquipmentMapping(): ApplicationDefaultParameterSetTagToEquipmentMapping {
    return BeaconCuttingData.CutterApplicationDefaultParameterSetTagEquipmentMapping;
  }

  public getApplicationDefaultParameterSetImplicitTags(
    _item: FlowItem<string>
  ): Record<string, unknown> {
    return {
      [BeaconCuttingData.ApplicationDefaultParameterSetTag.CUTTER]:
        this.getEquipmentName(),
    };
  }

  public getApplicationDefaultParameterSetSubheadingFromBucket(
    bucket: ScratchpadBucket
  ): string {
    const subheadingParts: string[] = [];

    const tagMapping =
      this.getApplicationDefaultParameterSetTagToEquipmentMapping();
    const defaultTags = tagMapping[this.getEquipmentName()];
    const savedTags = bucket.tags;

    const parameterSetData = bucket.data as ApplicationDefaultParameterSetData;

    const item: FlowItem = {
      ref: BeaconCuttingData.FlowItemRef.CUT,
      parameters: parameterSetData.parameters,
    };

    const process = this.flowItemService.getFlowItemPickerLabel(
      item,
      BeaconCuttingData.FlowItemParameterRef.PROCESS
    );

    if (process) {
      subheadingParts.push(process);
    }

    const tagValues: string[] = Object.entries(savedTags).reduce(
      (vals, [name, val]) => {
        if (name in defaultTags) {
          const label = defaultTags[name].options.find((o) => o.value === val)
            ?.label;

          if (label) {
            const localized = getLocalizedText(label);

            if (localized) {
              vals.push(localized);
            }
          }
        }

        return vals;
      },
      [] as string[]
    );

    if (tagValues.length > 0) {
      subheadingParts.push(...tagValues);
    }

    return subheadingParts.join(', ');
  }

  public getEquipmentName(): string {
    return this.getLicense().cutter;
  }

  protected getLicense(): BeaconCuttingData.License {
    return this.license.licenseFormValues as BeaconCuttingData.License;
  }

  protected getFlowConfigForEquipment(): Partial<BeaconCuttingData.FlowConfig> {
    const equipment = this.getEquipmentName();

    if (equipment === BeaconCuttingData.Cutter.HYPERTHERM_POWERMAX_SYNC) {
      return {
        current: BeaconCuttingData.Current.A_85,
      };
    } else if (
      equipment === BeaconCuttingData.Cutter.THERMAL_DYNAMICS_CUTMASTER
    ) {
      return {
        current: BeaconCuttingData.Current.A_80,
      };
    }

    return {};
  }
}

export { CuttingFlowServiceImpl };
